import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Send,
  MessageCircle,
  X,
  Info
} from 'lucide-react';

interface LectureDesignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const reasons = [
  'Je vis une période de flou et j\'ai besoin de clarté',
  'Je veux comprendre mes schémas relationnels',
  'Je suis en reconversion pro et je cherche mon alignement',
  'Je suis curieux(se) de mieux me connaître'
];

export function LectureDesignModal({ isOpen, onClose }: LectureDesignModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    reason: '',
    customReason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Simuler un délai d'envoi
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Demande envoyée !",
        description: "Nous vous contacterons dans les 24h avec les créneaux disponibles.",
      });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        reason: '',
        customReason: ''
      });

      // Fermer la modale
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du formulaire. Veuillez réessayer.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Réserver votre lecture</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Informations personnelles */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="w-4 h-4 inline mr-2" />
                        Nom complet
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Adresse e-mail
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Informations de naissance */}
                  <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                    <h3 className="font-semibold mb-4">Informations de naissance</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.birthDate}
                        onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Clock className="w-4 h-4 inline mr-2" />
                        Heure de naissance
                      </label>
                      <input
                        type="time"
                        required
                        value={formData.birthTime}
                        onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Lieu de naissance
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.birthPlace}
                        onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ville, Pays"
                      />
                    </div>
                  </div>

                  {/* Raison de la demande */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Pourquoi souhaitez-vous une lecture HED ?</h3>
                    <div className="space-y-2">
                      {reasons.map((reason, index) => (
                        <label key={index} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            name="reason"
                            value={reason}
                            checked={formData.reason === reason}
                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>{reason}</span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="reason"
                          value="other"
                          checked={formData.reason === 'other'}
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <span>Autre...</span>
                      </label>
                      {formData.reason === 'other' && (
                        <textarea
                          value={formData.customReason}
                          onChange={(e) => setFormData({ ...formData, customReason: e.target.value })}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Dites-nous en plus..."
                          rows={3}
                        />
                      )}
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="space-y-4">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#54075B] hover:bg-[#54075B]/90"
                    >
                      {submitting ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Envoi en cours...
                        </div>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer ma demande
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => window.location.href = "https://wa.me/votre-numero"}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Je préfère être guidé(e) par une assistante
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Une fois le formulaire envoyé, vous recevrez un e-mail sous 24h avec les créneaux disponibles pour votre séance.
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}